import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config/api';

const API = API_BASE;
const getToken = () => localStorage.getItem('gepetto_token') || sessionStorage.getItem('gepetto_token');
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

const TRIGGER_OPTIONS = [
    { value: 'registration_completed', label: 'Cadastro realizado', hint: 'Conta + workspace criados (auto cadastro ou Master).' },
    { value: 'plan_selected', label: 'Plano específico escolhido', hint: 'No cadastro, quando o plano for o selecionado abaixo.' },
    { value: 'profile_updated', label: 'Perfil do admin alterado', hint: 'Nome, cidade, estado, empresa ou telefone atualizados (API de perfil).' },
    { value: 'boleto_created', label: 'Boleto criado', hint: 'Webhook Asaas: pagamento BOLETO gerado (PAYMENT_CREATED).' },
    { value: 'payment_confirmed', label: 'Pagamento realizado', hint: 'Webhook Asaas (PAYMENT_CONFIRMED ou PAYMENT_RECEIVED), Stripe checkout, ou Master alterando o plano do cliente.' },
    { value: 'payment_cancelled', label: 'Pagamento cancelado', hint: 'Asaas PAYMENT_DELETED ou assinatura Stripe cancelada/inadimplente.' },
    { value: 'payment_expired', label: 'Pagamento expirado / vencido', hint: 'Asaas PAYMENT_OVERDUE.' },
    { value: 'plan_limit_reached', label: 'Limite do plano atingido', hint: 'Ex.: armazenamento de mídia ao tentar upload.' },
];

const ACTION_OPTIONS = [
    { value: 'add_lead', label: 'Adicionar lead', hint: 'Só insere lead novo na LL no gatilho “Cadastro realizado”; nos outros gatilhos equivale a atualizar (upsert).' },
    { value: 'update_lead', label: 'Atualizar lead', hint: 'Reenvia dados atuais do admin; o e-mail identifica o lead na máquina (conforme regras do Leadlovers).' },
    { value: 'remove_lead', label: 'Remover lead', hint: 'Remove o admin da máquina indicada neste fluxo.' },
];

const LIMIT_KIND_OPTIONS = [
    { value: 'storage_media', label: 'Armazenamento de mídia' },
];

function logRowTooltip(row) {
    const chunks = [];
    if (row.payload) {
        try {
            const p = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload;
            chunks.push(JSON.stringify(p, null, 2));
        } catch {
            chunks.push(String(row.payload));
        }
    }
    if (row.error && row.error !== 'OK') chunks.push(`Erro: ${row.error}`);
    const s = chunks.join('\n\n');
    return s.length > 2500 ? `${s.slice(0, 2500)}…` : s;
}

function emptyDraft() {
    return {
        name: '',
        trigger: 'registration_completed',
        planId: '',
        limitKind: '',
        action: 'add_lead',
        machineCode: '',
        sequenceCode: '',
        levelCode: '1',
        machineName: '',
        sequenceName: '',
        emailName: '',
        enabled: true,
    };
}

function newFlowId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `flow_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export default function LeadloversIntegrationPanel() {
    const [status, setStatus] = useState(null);
    const [flows, setFlows] = useState([]);
    const [draft, setDraft] = useState(emptyDraft);
    const [editingId, setEditingId] = useState(null);
    const [machines, setMachines] = useState([]);
    const [sequences, setSequences] = useState([]);
    const [levels, setLevels] = useState([]);
    const [catalogHint, setCatalogHint] = useState('');
    const [seqHint, setSeqHint] = useState('');
    const [levelsHint, setLevelsHint] = useState('');
    const [plans, setPlans] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [pushingLeads, setPushingLeads] = useState(false);
    const [captureGlobal, setCaptureGlobal] = useState({
        machineCode: '',
        sequenceCode: '',
        levelCode: '1',
    });
    const [savingCapture, setSavingCapture] = useState(false);
    const [msg, setMsg] = useState('');

    const loadFlows = useCallback(async () => {
        const r = await fetch(`${API}/master/leadlovers/flows`, { headers: authHeaders() });
        const d = await r.json();
        if (r.ok) {
            const list = Array.isArray(d.flows) ? d.flows : [];
            setFlows(list.map((f, i) => ({ ...f, sortOrder: f.sortOrder ?? i })));
        }
    }, []);

    const loadCatalog = useCallback(async () => {
        const r = await fetch(`${API}/master/leadlovers/catalog`, { headers: authHeaders() });
        const d = await r.json();
        setMachines(d.machines || []);
        setCatalogHint(d.hint || (d.errors?.length ? d.errors.join(' · ') : '') || '');
    }, []);

    const loadPlans = useCallback(async () => {
        const r = await fetch(`${API}/master/plans`, { headers: authHeaders() });
        const d = await r.json();
        if (r.ok) setPlans(d.plans || []);
    }, []);

    const loadLogs = useCallback(async () => {
        const r = await fetch(`${API}/master/leadlovers/logs?limit=80`, { headers: authHeaders() });
        const d = await r.json();
        if (r.ok) setLogs(d.logs || []);
    }, []);

    const loadLeadloversConfig = useCallback(async () => {
        const r = await fetch(`${API}/master/leadlovers/config`, { headers: authHeaders() });
        const d = await r.json();
        if (!r.ok) return;
        const c = d.capture || {};
        setCaptureGlobal({
            machineCode: c.machineCode != null ? String(c.machineCode) : '',
            sequenceCode: c.sequenceCode != null ? String(c.sequenceCode) : '',
            levelCode: c.levelCode != null ? String(c.levelCode) : '1',
        });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const s = await fetch(`${API}/master/leadlovers/status`, { headers: authHeaders() }).then((r) => r.json());
            setStatus(s);
            if (s.enabled) {
                await Promise.all([
                    loadFlows(),
                    loadCatalog(),
                    loadPlans(),
                    loadLogs(),
                    loadLeadloversConfig(),
                ]);
            } else {
                setFlows([]);
                setMachines([]);
                setLogs([]);
            }
        } catch {
            setStatus({ enabled: false });
        }
        setLoading(false);
    }, [loadFlows, loadCatalog, loadPlans, loadLogs, loadLeadloversConfig]);

    useEffect(() => {
        load();
    }, [load]);

    const mc = parseInt(draft.machineCode, 10);
    const sc = parseInt(draft.sequenceCode, 10);

    useEffect(() => {
        if (!status?.enabled || !mc) {
            setSequences([]);
            setSeqHint('');
            return;
        }
        let cancelled = false;
        (async () => {
            const r = await fetch(`${API}/master/leadlovers/sequences/${mc}`, { headers: authHeaders() });
            const d = await r.json();
            if (cancelled) return;
            setSequences(d.sequences || []);
            setSeqHint(d.hint || '');
        })();
        return () => { cancelled = true; };
    }, [status?.enabled, mc]);

    useEffect(() => {
        if (!status?.enabled || !mc || !sc) {
            setLevels([]);
            setLevelsHint('');
            return;
        }
        let cancelled = false;
        (async () => {
            const r = await fetch(`${API}/master/leadlovers/sequence-levels/${mc}/${sc}`, { headers: authHeaders() });
            const d = await r.json();
            if (cancelled) return;
            setLevels(d.levels || []);
            setLevelsHint(d.hint || '');
        })();
        return () => { cancelled = true; };
    }, [status?.enabled, mc, sc]);

    const sortedFlows = useMemo(
        () => [...flows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        [flows]
    );

    const persistFlows = async (nextFlows) => {
        setSaving(true);
        setMsg('');
        try {
            const indexed = nextFlows.map((f, i) => ({ ...f, sortOrder: i }));
            const r = await fetch(`${API}/master/leadlovers/flows`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ flows: indexed }),
            });
            const d = await r.json();
            if (r.ok) {
                setFlows(d.flows || indexed);
                setMsg('✅ Fluxos salvos.');
                loadLogs();
            } else setMsg('❌ ' + (d.error || 'Erro ao salvar'));
        } catch {
            setMsg('❌ Erro de rede');
        }
        setSaving(false);
    };

    const handleGravarFluxo = () => {
        if (!draft.machineCode || !draft.sequenceCode) {
            setMsg('❌ Informe máquina e sequência (códigos numéricos).');
            return;
        }
        const levelNum = parseInt(draft.levelCode, 10);
        if (!Number.isFinite(levelNum) || levelNum < 1) {
            setMsg('❌ Nível do e-mail inválido (mínimo 1).');
            return;
        }
        if (draft.trigger === 'plan_selected' && !draft.planId) {
            setMsg('❌ Para “Plano específico”, escolha o plano.');
            return;
        }
        if (draft.trigger === 'plan_limit_reached' && !draft.limitKind) {
            setMsg('❌ Para “Limite do plano”, escolha o tipo de limite.');
            return;
        }

        const row = {
            ...draft,
            id: editingId || newFlowId(),
            sortOrder: editingId != null ? flows.find((f) => f.id === editingId)?.sortOrder ?? flows.length : flows.length,
            machineCode: parseInt(draft.machineCode, 10),
            sequenceCode: parseInt(draft.sequenceCode, 10),
            levelCode: levelNum,
            enabled: draft.enabled !== false,
        };

        let next;
        if (editingId) {
            next = flows.map((f) => (f.id === editingId ? row : f));
        } else {
            next = [...flows, row];
        }
        setEditingId(null);
        setDraft(emptyDraft());
        persistFlows(next);
    };

    const editFlow = (f) => {
        setEditingId(f.id);
        setDraft({
            name: f.name || '',
            trigger: f.trigger || 'registration_completed',
            planId: f.planId || '',
            limitKind: f.limitKind || '',
            action: f.action || 'add_lead',
            machineCode: f.machineCode != null ? String(f.machineCode) : '',
            sequenceCode: f.sequenceCode != null ? String(f.sequenceCode) : '',
            levelCode: f.levelCode != null ? String(f.levelCode) : '1',
            machineName: f.machineName || '',
            sequenceName: f.sequenceName || '',
            emailName: f.emailName || '',
            enabled: f.enabled !== false,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const duplicateFlow = (f) => {
        const copy = {
            ...f,
            id: newFlowId(),
            name: (f.name || 'Fluxo') + ' (cópia)',
            sortOrder: flows.length,
        };
        persistFlows([...flows, copy]);
    };

    const deleteFlow = (id) => {
        if (!window.confirm('Excluir este fluxo?')) return;
        persistFlows(flows.filter((f) => f.id !== id));
    };

    const moveFlow = (id, dir) => {
        const list = sortedFlows.map((f) => ({ ...f }));
        const idx = list.findIndex((f) => f.id === id);
        const j = idx + dir;
        if (idx < 0 || j < 0 || j >= list.length) return;
        [list[idx], list[j]] = [list[j], list[idx]];
        persistFlows(list);
    };

    const handleTest = async () => {
        setTesting(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/leadlovers/test`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (r.ok) setMsg(`✅ ${d.message || 'Conexão OK.'}`);
            else setMsg('❌ ' + (d.error || 'Falha'));
        } catch {
            setMsg('❌ Erro de rede');
        }
        setTesting(false);
    };

    const saveCaptureGlobal = async () => {
        setSavingCapture(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/leadlovers/config`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                    capture: {
                        machineCode: captureGlobal.machineCode,
                        sequenceCode: captureGlobal.sequenceCode,
                        levelCode: captureGlobal.levelCode || '1',
                    },
                }),
            });
            const d = await r.json();
            if (r.ok) setMsg('✅ Captura global salva (máquina/sequência para sync e “Sincronizar todos os leads”).');
            else setMsg('❌ ' + (d.error || 'Erro ao salvar'));
        } catch {
            setMsg('❌ Erro de rede');
        }
        setSavingCapture(false);
    };

    const handlePushAllLeads = async () => {
        if (!window.confirm(
            'Reenviar todos os workspaces para o Leadlovers?\n\n'
            + 'Usa: (1) máquina/sequência da “Captura global” abaixo, ou (2) variáveis no .env, ou (3) o primeiro fluxo ativo que tenha máquina+sequência.\n\n'
            + 'Dados: nome, e-mail, cidade, estado e empresa do admin.'
        )) return;
        setPushingLeads(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/leadlovers/push-all-leads`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (r.ok) {
                const src =
                    d.captureSource === 'first_automation_flow'
                        ? 'origem: 1º fluxo ativo'
                        : d.captureSource === 'settings_or_env'
                          ? 'origem: captura global / .env'
                          : '';
                const cap =
                    d.capture && d.capture.machineCode
                        ? ` [Máq. ${d.capture.machineCode} · Seq. ${d.capture.sequenceCode} · Nível ${d.capture.levelCode}]`
                        : '';
                let text = `✅ Sincronização: ${d.ok} OK · ${d.fail} falha(s) · ${d.processed} processado(s).${cap}${src ? ` (${src})` : ''}`;
                if (d.errors?.length) {
                    const sample = d.errors.slice(0, 4).map((e) => `${e.email}: ${e.error}`).join(' | ');
                    text += ` Ex.: ${sample}`;
                }
                setMsg(text);
                loadLogs();
            } else {
                setMsg('❌ ' + (d.error || 'Falha ao sincronizar'));
            }
        } catch {
            setMsg('❌ Erro de rede');
        }
        setPushingLeads(false);
    };

    const triggerLabel = (v) => TRIGGER_OPTIONS.find((t) => t.value === v)?.label || v;

    if (loading) return <div className="p-8 text-slate-500 dark:text-slate-400">Carregando…</div>;

    return (
        <div className="max-w-6xl pb-10">
            <h2 className="m-0 mb-2 text-2xl font-bold text-slate-800 dark:text-slate-100">Leadlovers — Automações</h2>
            <p className="m-0 mb-4 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                Monte fluxos: <strong>gatilho</strong> no Geppetto → <strong>ação</strong> no Leadlovers (máquina, sequência, e-mail).
                Os códigos de máquina/sequência vêm da API quando ela listar; senão, use os números exibidos no painel Leadlovers.
                <span className="block mt-2">
                    Em <strong>Adicionar lead</strong> e <strong>Atualizar lead</strong>, o Geppetto envia sempre a tag <strong>Geppetto</strong> no mesmo pedido da Leadlovers (nome idêntico ao da sua conta). Para outro rótulo, use <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">LEADLOVERS_LEAD_TAG_NAME</code> no .env do backend.
                </span>
                <span className="block mt-2">
                    O <strong>e-mail do admin</strong> do workspace é o identificador estável: em <em>Atualizar lead</em> reenviamos os dados cadastrais atuais (nome, cidade, estado, empresa) para o Leadlovers reconhecer o mesmo lead pela chave de e-mail dentro da <strong>máquina</strong>.
                    A <strong>sequência</strong> e o <strong>nível</strong> em cada fluxo definem qual automação/disparo no LL você associa àquele gatilho.
                </span>
            </p>

            {msg && (
                <div
                    className={`p-3 rounded-lg text-sm font-medium mb-4 ${
                        msg.startsWith('✅')
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                    }`}
                >
                    {msg}
                </div>
            )}

            <div className="flex flex-wrap gap-2 mb-6">
                <button
                    type="button"
                    disabled={!status?.enabled || testing}
                    onClick={handleTest}
                    className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-sm font-semibold text-slate-800 dark:text-slate-100 border-none cursor-pointer disabled:opacity-50"
                >
                    {testing ? 'Testando…' : 'Testar conexão API'}
                </button>
                <button
                    type="button"
                    disabled={!status?.enabled}
                    onClick={() => loadLogs()}
                    className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-sm font-semibold border-none cursor-pointer disabled:opacity-50"
                >
                    Atualizar logs
                </button>
                <button
                    type="button"
                    disabled={!status?.enabled}
                    onClick={() => loadCatalog()}
                    className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-sm font-semibold border-none cursor-pointer disabled:opacity-50"
                >
                    Recarregar máquinas
                </button>
                <button
                    type="button"
                    disabled={!status?.enabled || pushingLeads}
                    onClick={handlePushAllLeads}
                    className="px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold border-none cursor-pointer disabled:opacity-50"
                    title="Usa captura global, .env ou o primeiro fluxo ativo com máquina+sequência"
                >
                    {pushingLeads ? 'Sincronizando leads…' : 'Sincronizar todos os leads (captura)'}
                </button>
            </div>

            {status?.enabled && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6 text-sm text-amber-950 dark:text-amber-100">
                    <h3 className="m-0 mb-2 text-base font-semibold">Captura global (recomendado)</h3>
                    <p className="m-0 mb-3 text-amber-900/90 dark:text-amber-200/90 leading-relaxed">
                        Os <strong>fluxos</strong> da tabela abaixo são independentes. O botão <strong>Sincronizar todos os leads</strong> e o sync legado de tenants usam estes números — ou o <strong>primeiro fluxo ativo</strong> se aqui estiver vazio e no .env também.
                        Não é obrigatório usar .env se você preencher e salvar aqui.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-3 mb-3">
                        <div>
                            <label className="text-xs font-semibold text-amber-900 dark:text-amber-200 block mb-1">Máquina</label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 rounded-md border border-amber-200 dark:border-amber-700 bg-white dark:bg-slate-900 text-sm"
                                value={captureGlobal.machineCode}
                                onChange={(e) => setCaptureGlobal((c) => ({ ...c, machineCode: e.target.value }))}
                                placeholder="ex. 774881"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-amber-900 dark:text-amber-200 block mb-1">Sequência</label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 rounded-md border border-amber-200 dark:border-amber-700 bg-white dark:bg-slate-900 text-sm"
                                value={captureGlobal.sequenceCode}
                                onChange={(e) => setCaptureGlobal((c) => ({ ...c, sequenceCode: e.target.value }))}
                                placeholder="ex. 1838945"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-amber-900 dark:text-amber-200 block mb-1">Nível</label>
                            <input
                                type="number"
                                min={1}
                                className="w-full px-3 py-2 rounded-md border border-amber-200 dark:border-amber-700 bg-white dark:bg-slate-900 text-sm"
                                value={captureGlobal.levelCode}
                                onChange={(e) => setCaptureGlobal((c) => ({ ...c, levelCode: e.target.value }))}
                                placeholder="1"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={savingCapture}
                        onClick={saveCaptureGlobal}
                        className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold border-none cursor-pointer disabled:opacity-50"
                    >
                        {savingCapture ? 'Salvando…' : 'Salvar captura global'}
                    </button>
                </div>
            )}

            {!status?.enabled && (
                <p className="text-amber-600 dark:text-amber-400 text-sm">Configure <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">LEADLOVERS_TOKEN</code> no .env.</p>
            )}

            {status?.enabled && (
                <>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm mb-8">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div className="min-w-0 flex-1">
                                <h3 className="m-0 text-lg font-semibold text-slate-800 dark:text-slate-200">
                                    {editingId ? 'Editar fluxo' : 'Novo fluxo'}
                                </h3>
                                <p className="text-xs text-slate-500 m-0 mt-1 leading-relaxed">
                                    Preencha gatilho, ação e códigos de máquina/sequência/nível. A tag <strong>Geppetto</strong> é enviada automaticamente no lead (exceto em &quot;Remover lead&quot;).
                                </p>
                            </div>
                            {editingId && (
                                <button
                                    type="button"
                                    className="shrink-0 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700"
                                    onClick={() => {
                                        setEditingId(null);
                                        setDraft(emptyDraft());
                                    }}
                                >
                                    Cancelar edição
                                </button>
                            )}
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Nome do fluxo (opcional)</label>
                                <input
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                                    value={draft.name}
                                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                                    placeholder="Ex.: Boas-vindas plano Free"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Gatilho (evento)</label>
                                <select
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                    value={draft.trigger}
                                    onChange={(e) => setDraft((d) => ({ ...d, trigger: e.target.value }))}
                                >
                                    {TRIGGER_OPTIONS.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mt-1 m-0">{TRIGGER_OPTIONS.find((t) => t.value === draft.trigger)?.hint}</p>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Ação</label>
                                <select
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                    value={draft.action}
                                    onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
                                >
                                    {ACTION_OPTIONS.map((a) => (
                                        <option key={a.value} value={a.value}>{a.label}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mt-1 m-0">{ACTION_OPTIONS.find((a) => a.value === draft.action)?.hint}</p>
                            </div>

                            {draft.trigger === 'plan_selected' && (
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Plano</label>
                                    <select
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                        value={draft.planId}
                                        onChange={(e) => setDraft((d) => ({ ...d, planId: e.target.value }))}
                                    >
                                        <option value="">Selecione o plano…</option>
                                        {plans.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {draft.trigger === 'plan_limit_reached' && (
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Tipo de limite</label>
                                    <select
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                        value={draft.limitKind}
                                        onChange={(e) => setDraft((d) => ({ ...d, limitKind: e.target.value }))}
                                    >
                                        <option value="">Selecione…</option>
                                        {LIMIT_KIND_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Máquina</label>
                                {machines.length > 0 && (
                                    <select
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm mb-2"
                                        value={machines.some((m) => String(m.code) === String(draft.machineCode)) ? String(draft.machineCode) : ''}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            const m = machines.find((x) => String(x.code) === v);
                                            setDraft((d) => ({
                                                ...d,
                                                machineCode: v,
                                                machineName: m?.name || '',
                                                sequenceCode: '',
                                                sequenceName: '',
                                                levelCode: '1',
                                                emailName: '',
                                            }));
                                        }}
                                    >
                                        <option value="">Selecione na lista…</option>
                                        {machines.map((m) => (
                                            <option key={m.code} value={m.code}>{m.name} — código {m.code}</option>
                                        ))}
                                    </select>
                                )}
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                    placeholder="Código da máquina (Leadlovers)"
                                    value={draft.machineCode}
                                    onChange={(e) => setDraft((d) => ({ ...d, machineCode: e.target.value }))}
                                />
                                {catalogHint && <p className="text-[11px] text-amber-700 dark:text-amber-300 m-0 mt-1">{catalogHint}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Sequência de e-mails</label>
                                {sequences.length > 0 && (
                                    <select
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm mb-2"
                                        value={sequences.some((s) => String(s.code) === String(draft.sequenceCode)) ? String(draft.sequenceCode) : ''}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            const s = sequences.find((x) => String(x.code) === v);
                                            setDraft((d) => ({
                                                ...d,
                                                sequenceCode: v,
                                                sequenceName: s?.name || '',
                                                levelCode: '1',
                                                emailName: '',
                                            }));
                                        }}
                                    >
                                        <option value="">Selecione…</option>
                                        {sequences.map((s) => (
                                            <option key={s.code} value={s.code}>{s.name} — código {s.code}</option>
                                        ))}
                                    </select>
                                )}
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                    placeholder="Código da sequência (EmailSequenceCode)"
                                    value={draft.sequenceCode}
                                    onChange={(e) => setDraft((d) => ({ ...d, sequenceCode: e.target.value }))}
                                />
                                {seqHint && <p className="text-[11px] text-slate-400 m-0 mt-1">{seqHint}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">E-mail (nível na sequência)</label>
                                {levels.length > 0 && (
                                    <select
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm mb-2"
                                        value={levels.some((l) => String(l.levelCode) === String(draft.levelCode)) ? String(draft.levelCode) : ''}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            const lv = levels.find((x) => String(x.levelCode) === v);
                                            setDraft((d) => ({
                                                ...d,
                                                levelCode: v,
                                                emailName: lv?.name || '',
                                            }));
                                        }}
                                    >
                                        <option value="">Selecione…</option>
                                        {levels.map((l) => (
                                            <option key={l.levelCode} value={l.levelCode}>
                                                Nível {l.levelCode}: {l.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <input
                                    type="number"
                                    min={1}
                                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
                                    placeholder="SequenceLevelCode (geralmente 1 = primeiro e-mail)"
                                    value={draft.levelCode}
                                    onChange={(e) => setDraft((d) => ({ ...d, levelCode: e.target.value }))}
                                />
                                {levelsHint && <p className="text-[11px] text-slate-400 m-0 mt-1">{levelsHint}</p>}
                            </div>

                            <div className="md:col-span-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="flow-enabled"
                                    checked={draft.enabled !== false}
                                    onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                                />
                                <label htmlFor="flow-enabled" className="text-sm text-slate-600 dark:text-slate-300">Fluxo ativo</label>
                            </div>
                        </div>

                        <button
                            type="button"
                            disabled={saving}
                            onClick={handleGravarFluxo}
                            className="mt-5 px-6 py-2.5 rounded-lg bg-blue-600 text-white font-bold text-sm border-none cursor-pointer disabled:opacity-60"
                        >
                            {saving ? 'Salvando…' : 'Gravar fluxo'}
                        </button>
                    </div>

                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden mb-8">
                        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 font-semibold text-slate-800 dark:text-slate-200">
                            Fluxos configurados (ordem de execução)
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="text-xs text-slate-500 border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50">
                                        <th className="py-2 px-3 font-semibold">#</th>
                                        <th className="py-2 px-3 font-semibold">Nome</th>
                                        <th className="py-2 px-3 font-semibold">Gatilho</th>
                                        <th className="py-2 px-3 font-semibold">Ação</th>
                                        <th className="py-2 px-3 font-semibold">Máquina</th>
                                        <th className="py-2 px-3 font-semibold">Seq.</th>
                                        <th className="py-2 px-3 font-semibold">Nível</th>
                                        <th className="py-2 px-3 font-semibold">Ativo</th>
                                        <th className="py-2 px-3 font-semibold w-40">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedFlows.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="py-8 px-3 text-center text-slate-400">Nenhum fluxo ainda. Crie um acima.</td>
                                        </tr>
                                    ) : (
                                        sortedFlows.map((f, i) => (
                                            <tr key={f.id} className="border-b border-slate-100 dark:border-slate-700/80 text-slate-700 dark:text-slate-300">
                                                <td className="py-2 px-3 text-xs">{i + 1}</td>
                                                <td className="py-2 px-3 max-w-[140px] truncate" title={f.name}>{f.name || '—'}</td>
                                                <td className="py-2 px-3 text-xs max-w-[160px]">
                                                    {triggerLabel(f.trigger)}
                                                    {f.trigger === 'plan_selected' && f.planId && (
                                                        <span className="block text-slate-400 truncate" title={f.planId}>{f.planId}</span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3 text-xs">{ACTION_OPTIONS.find((a) => a.value === f.action)?.label || f.action}</td>
                                                <td className="py-2 px-3 text-xs font-mono">{f.machineCode}</td>
                                                <td className="py-2 px-3 text-xs font-mono">{f.sequenceCode}</td>
                                                <td className="py-2 px-3 text-xs font-mono">{f.levelCode}</td>
                                                <td className="py-2 px-3">{f.enabled === false ? 'Não' : 'Sim'}</td>
                                                <td className="py-2 px-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        <button type="button" title="Subir" className="px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 border-none cursor-pointer" onClick={() => moveFlow(f.id, -1)} disabled={saving || i === 0}>↑</button>
                                                        <button type="button" title="Descer" className="px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 border-none cursor-pointer" onClick={() => moveFlow(f.id, 1)} disabled={saving || i === sortedFlows.length - 1}>↓</button>
                                                        <button type="button" title="Editar" className="px-1.5 py-0.5 text-xs rounded bg-sky-100 dark:bg-sky-900/50 border-none cursor-pointer" onClick={() => editFlow(f)} disabled={saving}>✎</button>
                                                        <button type="button" title="Duplicar" className="px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 border-none cursor-pointer" onClick={() => duplicateFlow(f)} disabled={saving}>⎘</button>
                                                        <button type="button" title="Excluir" className="px-1.5 py-0.5 text-xs rounded bg-red-100 dark:bg-red-900/40 border-none cursor-pointer" onClick={() => deleteFlow(f.id)} disabled={saving}>🗑</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
                        <h3 className="m-0 mb-3 text-lg font-semibold text-slate-800 dark:text-slate-200">Logs recentes</h3>
                        <p className="text-xs text-slate-500 mb-3 m-0">Sync Leadlovers e execução dos fluxos.</p>
                        {logs.length === 0 ? (
                            <p className="text-sm text-slate-400 m-0">Sem eventos.</p>
                        ) : (
                            <div className="overflow-x-auto max-h-72 overflow-y-auto text-xs">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-600">
                                            <th className="py-1.5 pr-2 font-semibold">Quando</th>
                                            <th className="py-1.5 pr-2 font-semibold">Evento</th>
                                            <th className="py-1.5 pr-2 font-semibold">Workspace</th>
                                            <th className="py-1.5 pr-2 font-semibold">Erro / status</th>
                                            <th className="py-1.5 font-semibold w-20">Copiar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((row) => (
                                            <tr key={row.id} className="border-b border-slate-100 dark:border-slate-700" title={logRowTooltip(row)}>
                                                <td className="py-1.5 pr-2 whitespace-nowrap text-slate-500">
                                                    {row.sent_at || row.created_at
                                                        ? new Date(row.sent_at || row.created_at).toLocaleString('pt-BR', {
                                                              day: '2-digit', month: '2-digit', year: 'numeric',
                                                              hour: '2-digit', minute: '2-digit', second: '2-digit'
                                                          })
                                                        : '—'}
                                                </td>
                                                <td className="py-1.5 pr-2">{row.event_type}</td>
                                                <td className="py-1.5 pr-2 max-w-[200px] truncate" title={row.tenant_name}>{row.tenant_name || row.tenant_id || '—'}</td>
                                                <td className="py-1.5 pr-2 text-red-600 dark:text-red-400 max-w-xs truncate" title={row.error || ''}>{row.error || 'OK'}</td>
                                                <td className="py-1.5">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-0.5 rounded text-[11px] font-semibold border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
                                                        onClick={async () => {
                                                            const text = logRowTooltip(row);
                                                            try {
                                                                await navigator.clipboard.writeText(text);
                                                                setMsg('✅ Log copiado para a área de transferência.');
                                                            } catch {
                                                                setMsg('❌ Não foi possível copiar (permissão do navegador).');
                                                            }
                                                        }}
                                                    >
                                                        Copiar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
